import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import WhatIfAnalysisLogo from 'public/images/what-if-analysis-logo.svg';
import React, { FC, useState } from 'react';

import { AssetDetails } from '@/api/asset/asset-schema';
import { config } from '@/config/app.config';
import { createLogGA } from '@/utils/analytics/logGA';

import { FaqDrawer } from '../faq-drawer/FaqDrawer';
import { Search } from '../search/Search';
import * as S from './Header.styles';

export interface HeaderProps {
  assetDetails?: AssetDetails;
  is404: boolean;
}

export const Header: FC<HeaderProps> = ({ assetDetails, is404 }) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const [isFaqDrawerOpen, setIsFaqDrawerOpen] = useState(false);

  const isAssetPage = router.pathname === '/[asset]';

  const clickFaq = () => {
    setIsFaqDrawerOpen(true);
    createLogGA('FAQs')('click_FAQs', { location: 'header' });
  };

  const clickExploreMore = () => {
    createLogGA('Headers')('click_explore_more', {
      location: 'assetPageHeader',
    });
  };

  const clickLogo = () => {
    createLogGA('Headers')('click_whatIf', {
      location: isAssetPage ? 'assetPageHeader' : 'landingPage',
    });
  };

  return (
    <>
      <S.Container data-testid="header">
        <S.LogoContainer>
          <Link
            href={'/'}
            onClick={clickLogo}
            data-testid="logo-link"
            aria-label="Earnings Search Page"
          >
            <WhatIfAnalysisLogo alt={t('whatIfAlt')} data-testid="logo-image" />
          </Link>
        </S.LogoContainer>

        {isAssetPage || is404 ? (
          <S.SearchContainer>
            <Search isHeaderSearch ticker={assetDetails?.company_name} />
          </S.SearchContainer>
        ) : null}

        <S.ButtonsContainer>
          <S.FaqLink
            data-testid="faqs-button"
            variant="secondary"
            size="small"
            onClick={clickFaq}
          >
            {t('faq')}
          </S.FaqLink>
          {isAssetPage && (
            <S.ExploreMore
              data-testid="explore-more-button"
              variant="secondary"
              size="small"
              as="a"
              href={config.urls.website}
              target="_blank"
              rel="noreferrer"
              onClick={clickExploreMore}
            >
              {t('exploreMoreFeatures')}
            </S.ExploreMore>
          )}
        </S.ButtonsContainer>
      </S.Container>
      <FaqDrawer
        isOpen={isFaqDrawerOpen}
        closeDrawer={() => setIsFaqDrawerOpen(false)}
      />
    </>
  );
};
